import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import { formattersFor, type Formatters } from './format';
import { detectLocale, isLocale, type Locale } from './locales';
import { MESSAGES, type Messages } from './messages';

const STORAGE_KEY = 'fleettrace.locale';

interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Messages;
  /**
   * Locale-aware numbers, dates and durations.
   *
   * <p>Lives beside the strings because it is the same decision. A Slovenian reader
   * given `87.3 km/h` reads eighty-seven thousand three hundred — the decimal comma is
   * the separator there and the dot is the thousands grouping, so a translated sentence
   * wrapped around an untranslated number states something false.
   */
  fmt: Formatters;
}

const I18nContext = createContext<I18nValue | null>(null);

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Private windows and blocked site data both throw. Fall through to detection.
  }
  return detectLocale();
}

export function I18nProvider({ children }: PropsWithChildren) {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);

  /**
   * `<html lang>` follows the locale on EVERY render path, not only on a change.
   *
   * <p>It was set inside `setLocale`, which meant it was correct only if you switched
   * language in this session: load the app with Slovenian already stored and the document
   * still declared itself English. Nothing visible breaks, which is the problem — a screen
   * reader then pronounces Slovenian with English phonology, and hyphenation and spell
   * check both use the wrong dictionary.
   */
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A locale that does not persist is better than a crash.
    }
  }, []);

  const value = useMemo<I18nValue>(
    () => ({ locale, setLocale, t: MESSAGES[locale], fmt: formattersFor(locale) }),
    [locale, setLocale],
  );

  return <I18nContext value={value}>{children}</I18nContext>;
}

export function useTranslation(): I18nValue {
  const value = use(I18nContext);
  if (!value) {
    throw new Error('useTranslation must be used within <I18nProvider>');
  }
  return value;
}
